import React from 'react'
import dayjs from 'dayjs';
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormDateControl } from '@agtech/web/components/Forms/Controls/AgTechFormDateControl';
import { AgTechFormReadOnlyTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { getContactFullName } from '@agtech/data/contacts/ContactData';
import { ContactDataGetContactDetailsQuery } from '@agtech/data/contacts/ContactDataQueries';
import { ContactNote } from '@agtech/data/contacts/notes/ContactNoteData';
import { useContactNoteDataWriteNoteAction } from '@agtech/data/contacts/notes/ContactNoteDataActions';

export type ContactNoteDialogProps = {
    contactId: number,
    note?: ContactNote
}

const ContactNoteDialog = (props: ContactNoteDialogProps) => {
    const dialogQuery = useAgTechQuery({
        query: ContactDataGetContactDetailsQuery,
        queryContext: {
            contactId: props.contactId
        }
    });

    const dialogAction = useContactNoteDataWriteNoteAction(props.contactId);

    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: props.note ? 'Update Note' : 'Create Note',
                submitButtonText: props.note ? 'Update' : 'Create',
            })}
            dialogData={{
                query: dialogQuery,
                getDialogData: contactDetails => ({
                    contact: contactDetails.contact,
                    note: props.note ?? {
                        id: 0,
                        contactId: props.contactId,
                        note: '',
                        date: dayjs().format('MM-DD-YYYY')
                    }
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d.note
            }}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormRow>
                        <AgTechFormReadOnlyTextControl
                            entityType={formContext.entity}
                            caption='Contact'
                            value={e => getContactFullName(e.contact)}
                        />
                        <AgTechFormDateControl
                            entityType={formContext.entity}
                            caption='Date'
                            value={e => dayjs(e.note.date).format('MM-DD-YYYY')}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                date: val
                            })}
                        />
                    </AgTechFormRow>
                    <AgTechFormRow>
                        <AgTechFormTextAreaControl
                            entityType={formContext.entity}
                            caption='Note'
                            value={e => e.note.note}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                note: {
                                    ...e.note,
                                    note: val
                                }
                            })}
                            rows={5}
                            width={100} 
                            required={true}
                            focused={true}
                        />
                    </AgTechFormRow>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
};

ContactNoteDialog.Open = (e: AgTechClickEventHandler, props: ContactNoteDialogProps) => e.dialogs.openDialog({
    dialog: () => <ContactNoteDialog {...props} />
});

ContactNoteDialog.Button = (props: ContactNoteDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text={props.note ? 'Update Note' : 'Create Note'}
        icon={props.note ? 'fas fa-edit' : 'fas fa-plus'}
        {...props}
        action={e => ContactNoteDialog.Open(e, props)}
    />
);

export default ContactNoteDialog;